// Plan de Emergencias v2, PE-B2: simulacros (PO-17). Lógica pura, sin base de datos (M38).
//  - Tiempo total = hora del último en llegar al punto de encuentro − hora de la alarma (se calcula, no se guarda).
//  - Comparación con el plan (paso 3): el tiempo total frente a premovimiento + Togawa (= RSET − detección − alarma,
//    porque el simulacro empieza a contar en la alarma). Sin RSET (falta el premovimiento, PE-23): por validar.
//    El paso 3 calcula UN tiempo para todo el centro (no por sector): se compara con ese valor y la traza lo dice.
//  - Conteo en el punto de encuentro frente a participantes; brigadistas presentes frente a la brigada (informativo).
//  - Las acciones propuestas no se guardan solas: el usuario las confirma (como en el paso 5). Clave de origen
//    'simulacro:<id>:<n>' (1 = tiempo, 2 = conteo, 3 en adelante = hallazgos que agrega el usuario).
//  - PO-17: cumple con al menos un simulacro realizado con el informe firmado en los últimos 12 meses (DE 394
//    Art. 68, texto de la orden de JC: el decreto no está transcrito en docs/normativa); la RTQ 3 de Quito pide la
//    misma frecuencia (al menos uno anual, documentado), así que no hay un umbral mayor.

import { fechaEc } from '../fechas';
import type { Traza } from './pe-tipos';
import { doc, unirFuentes } from './pe-tipos';
import type { AccionEditable } from './pe-acciones';

// ── Catálogos ────────────────────────────────────────────────────────────────

export const TIPOS_SIMULACRO = ['evacuacion', 'incendio', 'sismo', 'derrame_quimico', 'fuga_gas', 'inundacion', 'emergencia_medica', 'otro'] as const;
export type TipoSimulacro = typeof TIPOS_SIMULACRO[number];
export const ETIQUETA_TIPO: Record<TipoSimulacro, string> = {
  evacuacion: 'Evacuación', incendio: 'Incendio', sismo: 'Sismo', derrame_quimico: 'Derrame químico', fuga_gas: 'Fuga de gas',
  inundacion: 'Inundación', emergencia_medica: 'Emergencia médica', otro: 'Otro',
};

// 'ejecutado' es el valor de M33 (lo usa la pantalla anterior); en pantalla se lee "Realizado".
export type EstadoSimulacro = 'programado' | 'ejecutado' | 'cancelado';
export const ETIQUETA_ESTADO: Record<EstadoSimulacro, string> = { programado: 'Programado', ejecutado: 'Realizado', cancelado: 'Cancelado' };

export type MetodoFirma = 'pantalla' | 'papel' | 'firmaec';
export const ETIQUETA_METODO_FIRMA: Record<MetodoFirma, string> = {
  pantalla: 'Firma en pantalla', papel: 'Papel firmado (escaneado)', firmaec: 'PDF firmado con FirmaEC',
};

export type RolFirma = 'sst' | 'representante';
export const ETIQUETA_ROL_FIRMA: Record<RolFirma, string> = { sst: 'Responsable de SST', representante: 'Representante legal' };

export interface FirmaInforme {
  rol: RolFirma;
  nombre: string;
  cedula: string;
  firmado_en: string;      // ISO
  hash: string;            // SHA-256 del contenido del informe + datos del firmante
  metodo: MetodoFirma;
  firma_path?: string;     // trazo PNG (pantalla)
  archivo_path?: string;   // papel escaneado o PDF FirmaEC
}

export type RolParticipante = 'participante' | 'brigadista' | 'evacuado' | 'jefe' | 'observador' | 'evaluador';
export const ETIQUETA_ROL_PARTICIPANTE: Record<RolParticipante, string> = {
  participante: 'Participante', brigadista: 'Brigadista', evacuado: 'Evacuado', jefe: 'Jefe de brigada', observador: 'Observador', evaluador: 'Evaluador',
};

// Columnas de emergency_drills que usa la v2 (fase 0 + M33 + M38).
export interface FilaSimulacro {
  id: string;
  company_id: string;
  plan_id: string | null;
  calendar_event_id: string | null;
  status: EstadoSimulacro;
  drill_date: string;
  scheduled_date: string | null;
  performed_date: string | null;
  start_time: string | null;
  drill_type: TipoSimulacro;
  drill_type_other: string | null;
  objective: string | null;
  scenario: string | null;
  threat_key: string | null;
  location: string | null;
  scope_all: boolean;
  scope_sector_ids: string[];
  announced: boolean | null;
  coordinator_worker_id: string | null;
  coordinator_role: string | null;
  observers: string | null;
  participants_count: number | null;
  expected_occupants: number | null;
  assembly_count: number | null;
  alarm_time: string | null;
  evacuation_start_time: string | null;
  last_arrival_time: string | null;
  incidents_notes: string | null;   // hallazgos
  lessons_learned: string | null;
  conclusion: string | null;
  report_signed_at: string | null;
  report_method: MetodoFirma | null;
  report_signatures: FirmaInforme[];
  report_pdf_path: string | null;
  report_hash: string | null;
  reopen_history: { reabierto_en: string; reabierto_por: string | null; motivo: string }[];
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export const tipoTexto = (s: Pick<FilaSimulacro, 'drill_type' | 'drill_type_other'>) =>
  s.drill_type === 'otro' && s.drill_type_other?.trim() ? `Otro: ${s.drill_type_other.trim()}` : ETIQUETA_TIPO[s.drill_type] ?? s.drill_type;

export const fechaSimulacro = (s: Pick<FilaSimulacro, 'performed_date' | 'scheduled_date' | 'drill_date'>) =>
  s.performed_date ?? s.scheduled_date ?? s.drill_date;

export const firmado = (s: Pick<FilaSimulacro, 'report_signed_at'>) => !!s.report_signed_at;

// AAAA-MM-DD → DD/MM/AAAA; un instante (report_signed_at), en el día de Ecuador (lib/fechas).
export { fechaEc };

const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ── Tiempos ──────────────────────────────────────────────────────────────────

// 'HH:MM' o 'HH:MM:SS' → segundos desde las 00:00. Inválida o vacía → null.
export function horaASegundos(h: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec((h ?? '').trim());
  if (!m) return null;
  const [hh, mm, ss] = [Number(m[1]), Number(m[2]), Number(m[3] ?? 0)];
  if (hh > 23 || mm > 59 || ss > 59) return null;
  return hh * 3600 + mm * 60 + ss;
}

export function segundosAHora(s: number): string {
  const t = ((Math.round(s) % 86400) + 86400) % 86400;
  return [Math.floor(t / 3600), Math.floor((t % 3600) / 60), t % 60].map(x => String(x).padStart(2, '0')).join(':');
}

// Cronómetro 'M:SS' o 'MM:SS' (minutos y segundos desde la alarma) → segundos. Inválido → null.
export function minSegASegundos(v: string | null | undefined): number | null {
  const m = /^(\d{1,3}):([0-5]\d)$/.exec((v ?? '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

// Para registrar con cronómetro: la hora del evento = hora de la alarma + minutos y segundos.
export const horaDesdeCronometro = (alarma: string, minSeg: string): string | null => {
  const a = horaASegundos(alarma), d = minSegASegundos(minSeg);
  return a === null || d === null ? null : segundosAHora(a + d);
};

export function formatoDuracion(s: number | null): string {
  if (s === null || !Number.isFinite(s)) return '—';
  const t = Math.round(s);
  return `${Math.floor(t / 60)} min ${String(t % 60).padStart(2, '0')} s`;
}

export interface TiempoTotal { segundos: number | null; error?: string }

// Tiempo total del simulacro: del sonido de la alarma a la llegada del último al punto de encuentro.
export function tiempoTotal(s: Pick<FilaSimulacro, 'alarm_time' | 'evacuation_start_time' | 'last_arrival_time'>): TiempoTotal {
  const a = horaASegundos(s.alarm_time), u = horaASegundos(s.last_arrival_time), i = horaASegundos(s.evacuation_start_time);
  if (a === null || u === null) return { segundos: null };
  if (u < a) return { segundos: null, error: 'La hora del último en llegar es anterior a la alarma.' };
  if (i !== null && (i < a || i > u)) return { segundos: u - a, error: 'El inicio de la evacuación debe estar entre la alarma y la llegada del último.' };
  return { segundos: u - a };
}

export function validarTiempos(s: Pick<FilaSimulacro, 'alarm_time' | 'evacuation_start_time' | 'last_arrival_time'>): string[] {
  const e: string[] = [];
  for (const [campo, v] of [['alarma', s.alarm_time], ['inicio de la evacuación', s.evacuation_start_time], ['último en llegar', s.last_arrival_time]] as const) {
    if (v && horaASegundos(v) === null) e.push(`La hora de ${campo} no es válida (usa HH:MM:SS).`);
  }
  const t = tiempoTotal(s);
  if (t.error) e.push(t.error);
  return e;
}

// ── Ocupantes esperados ──────────────────────────────────────────────────────

export interface SectorMin { id: string; name: string; occupants: number | null }

// Por defecto, la suma de ocupantes de los sectores del alcance (editable en el formulario).
export function ocupantesEsperados(sectores: SectorMin[], scopeAll: boolean, scopeIds: string[]): { total: number | null; sinDato: string[] } {
  const lista = scopeAll ? sectores : sectores.filter(s => scopeIds.includes(s.id));
  const sinDato = lista.filter(s => s.occupants === null || s.occupants === undefined).map(s => s.name);
  if (lista.length === 0) return { total: null, sinDato };
  const total = lista.reduce((n, s) => n + (s.occupants ?? 0), 0);
  return { total: lista.length === sinDato.length ? null : total, sinDato };
}

export function alcanceTexto(s: Pick<FilaSimulacro, 'scope_all' | 'scope_sector_ids'>, sectores: Pick<SectorMin, 'id' | 'name'>[]): string {
  if (s.scope_all) return 'Todo el centro';
  const nombres = s.scope_sector_ids.map(id => sectores.find(x => x.id === id)?.name ?? 'sector borrado');
  return nombres.length ? nombres.join(', ') : '—';
}

// ── Comparación con el plan ──────────────────────────────────────────────────

// Lo que el paso 3 guardó en emergency_assessments (method 'rset'): resumen e inputs.
export interface PlanEvacuacion {
  tsSegundos: number | null;      // Togawa
  rsetSegundos: number | null;    // null sin premovimiento (PE-23)
  deteccionS: number | null;
  alarmaS: number | null;
  // PE-B2b: de qué sale el RSET del paso 3 (Togawa: N personas, ancho A de la salida y recorrido D).
  personas?: number | null;
  anchoM?: number | null;
  distanciaM?: number | null;
}

export const FUENTE_RSET = unirFuentes(doc.matriz(53), doc.paso0('§7 PE-16 y PE-23'));
export const FUENTE_ANEXO1_PO17 = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo), PO 17'; // interno: docs/normativa/anexo1_lista_verificacion_sst.md:581
export const FUENTE_DE394_ART68 = 'Decreto Ejecutivo 394, Art. 68 (por validar)'; // interno: texto de la orden PE-B2; el decreto no está transcrito en docs/normativa (README:25)
// Líneas internas de RTQ3_2026.md: 1551 (11.5 j), 1931 (13.5 b), 1793 (12.5 b) y 1094 (8.5 b).
export const FUENTE_RTQ3_SIMULACRO = `${doc.rtq(3, 1551)}: 11.5 j (industrial), 13.5 b (oficinas), 12.5 b (mercantil) y 8.5 b (educación)`;

// Tiempo que el plan prevé desde la alarma hasta la llegada al punto de encuentro: premovimiento + Togawa.
export function tiempoPlanDesdeAlarma(p: PlanEvacuacion | null): number | null {
  if (!p || p.rsetSegundos === null || !Number.isFinite(p.rsetSegundos)) return null;
  return p.rsetSegundos - (p.deteccionS ?? 0) - (p.alarmaS ?? 0);
}

export type ClaveComparacion = 'tiempo' | 'conteo' | 'brigada';
export interface Comparacion { clave: ClaveComparacion; traza: Traza; propuesta?: string }

export function compararTiempo(totalS: number | null, plan: PlanEvacuacion | null, alcanceParcial: boolean): Comparacion {
  const base = {
    metodo: 'simulacro_tiempo', norma: 'RSET del centro calculado en el capítulo 6 (método técnico aceptado)',
    articulo_o_tabla: 'tiempo del simulacro (alarma → último en llegar) ≤ premovimiento + Togawa', unidad: '', fuente_doc: FUENTE_RSET,
  };
  const num = (v: number | null | undefined, unidad: string) => (v === null || v === undefined || !Number.isFinite(v) ? null : `${String(Math.round(v * 100) / 100).replace('.', ',')} ${unidad}`);
  const partes = [num(plan?.personas, 'ocupantes'), num(plan?.anchoM, 'm de ancho de salida'), num(plan?.distanciaM, 'm de recorrido')].filter(Boolean);
  const deDonde = partes.length ? ` Sale de ${partes.join(', ')} (Togawa, capítulo 6).` : '';
  const notaAlcance = alcanceParcial
    ? ' El capítulo 6 calcula un solo tiempo para todo el centro: se compara con ese valor aunque el simulacro haya sido de algunos sectores (por validar).'
    : '';
  const previsto = tiempoPlanDesdeAlarma(plan);
  if (totalS === null) {
    return { clave: 'tiempo', traza: { ...base, umbral: previsto === null ? null : formatoDuracion(previsto), valor: null, estado: 'por_validar', nota: 'Faltan la hora de la alarma o la del último en llegar al punto de encuentro.' } };
  }
  if (previsto === null) {
    const ts = plan?.tsSegundos ?? null;
    return {
      clave: 'tiempo', traza: {
        ...base, umbral: null, valor: formatoDuracion(totalS), estado: 'por_validar', paso: 3,
        nota: `Tiempo del simulacro ${formatoDuracion(totalS)}. El plan no tiene RSET (falta el premovimiento en el capítulo 6)${ts !== null ? `; Togawa solo: ${formatoDuracion(ts)}` : ''}. El simulacro sirve para ajustar el premovimiento.${notaAlcance}`,
      },
    };
  }
  const cumple = totalS <= previsto;
  const nota = `Simulacro ${formatoDuracion(totalS)} frente a ${formatoDuracion(previsto)} previstos: RSET del centro ${formatoDuracion(plan!.rsetSegundos)} menos detección y alarma.${deDonde}${notaAlcance}`;
  return {
    clave: 'tiempo', traza: { ...base, umbral: formatoDuracion(previsto), valor: formatoDuracion(totalS), estado: cumple ? 'cumple' : 'no_cumple', nota },
    propuesta: cumple ? undefined
      : `El simulacro tardó ${formatoDuracion(totalS)}, más que los ${formatoDuracion(previsto)} previstos en el plan: revisar rutas, señalización, alarma y entrenamiento, y ajustar el premovimiento del capítulo 6.`,
  };
}

export function compararConteo(conteo: number | null, participantes: number | null): Comparacion {
  const base = {
    metodo: 'simulacro_conteo', norma: 'Plan de emergencia: conteo en el punto de encuentro', articulo_o_tabla: 'conteo en el punto de encuentro = participantes',
    unidad: 'personas', fuente_doc: FUENTE_ANEXO1_PO17,
  };
  if (conteo === null || participantes === null) {
    return { clave: 'conteo', traza: { ...base, umbral: participantes, valor: conteo, estado: 'por_validar', nota: 'Falta el conteo en el punto de encuentro o el número de participantes.' } };
  }
  if (conteo < participantes) {
    const faltan = participantes - conteo;
    return {
      clave: 'conteo', traza: { ...base, umbral: participantes, valor: conteo, estado: 'no_cumple', nota: `${faltan === 1 ? 'Faltó' : 'Faltaron'} ${faltan} persona${faltan === 1 ? '' : 's'} en el punto de encuentro.` },
      propuesta: `En el punto de encuentro se contaron ${conteo} de ${participantes} participantes: revisar el procedimiento de conteo y de búsqueda de personas que no llegan.`,
    };
  }
  return {
    clave: 'conteo', traza: {
      ...base, umbral: participantes, valor: conteo, estado: conteo === participantes ? 'cumple' : 'informativo',
      nota: conteo === participantes ? 'Llegaron todos los participantes.' : 'Se contaron más personas que participantes registrados: revisa el registro.',
    },
  };
}

export function compararBrigada(presentes: number | null, brigadaPlan: number | null): Comparacion {
  return {
    clave: 'brigada', traza: {
      metodo: 'simulacro_brigada', norma: 'Brigada del plan', articulo_o_tabla: 'brigadistas presentes frente a la brigada del plan',
      umbral: brigadaPlan, valor: presentes, unidad: 'personas', estado: 'informativo', fuente_doc: doc.paso0('§7 PE-38'),
      nota: brigadaPlan === null ? 'La empresa no tiene brigada registrada.' : `${presentes ?? 0} de ${brigadaPlan} brigadistas presentes.`,
    },
  };
}

export function compararConPlan(s: FilaSimulacro, plan: PlanEvacuacion | null, brigadistasPresentes: number | null, brigadaPlan: number | null): Comparacion[] {
  return [
    compararTiempo(tiempoTotal(s).segundos, plan, !s.scope_all),
    compararConteo(s.assembly_count, s.participants_count),
    compararBrigada(brigadistasPresentes, brigadaPlan),
  ];
}

// Semáforo de la lista: el peor estado entre tiempo y conteo (la brigada es informativa). Sin realizar: pendiente.
export type Semaforo = 'cumple' | 'no_cumple' | 'por_validar' | 'pendiente';
export const ETIQUETA_SEMAFORO: Record<Semaforo, string> = { cumple: 'Cumple', no_cumple: 'No cumple', por_validar: 'Por validar', pendiente: 'Sin resultado' };
export function semaforo(s: Pick<FilaSimulacro, 'status'>, comparaciones: Comparacion[]): Semaforo {
  if (s.status !== 'ejecutado') return 'pendiente';
  const estados = comparaciones.filter(c => c.clave !== 'brigada').map(c => c.traza.estado);
  if (estados.includes('no_cumple')) return 'no_cumple';
  if (estados.includes('por_validar')) return 'por_validar';
  return 'cumple';
}

// ── Acciones de mejora ───────────────────────────────────────────────────────

const N_POR_CLAVE: Record<'tiempo' | 'conteo', number> = { tiempo: 1, conteo: 2 };
export const claveAccionSimulacro = (drillId: string, n: number) => `simulacro:${drillId}:${n}`;
export const esClaveSimulacro = (k: string | null | undefined) => !!k && k.startsWith('simulacro:');
export const origenSimulacro = (s: Pick<FilaSimulacro, 'performed_date' | 'scheduled_date' | 'drill_date'>) => `Simulacro ${fechaEc(fechaSimulacro(s))}`;

export const METODO_ACCION_SIMULACRO = 'simulacro';
export interface PropuestaSimulacro { sourceKey: string; norma: string; articulo: string; descripcion: string }

// Propuestas que todavía no están guardadas (no se duplican: la clave ya guardada no se vuelve a proponer).
export function accionesPropuestas(s: FilaSimulacro, comparaciones: Comparacion[], clavesGuardadas: Set<string>): PropuestaSimulacro[] {
  return comparaciones.flatMap(c => {
    if (!c.propuesta || (c.clave !== 'tiempo' && c.clave !== 'conteo')) return [];
    const sourceKey = claveAccionSimulacro(s.id, N_POR_CLAVE[c.clave]);
    if (clavesGuardadas.has(sourceKey)) return [];
    return [{ sourceKey, norma: origenSimulacro(s), articulo: c.clave === 'tiempo' ? 'Tiempo de evacuación' : 'Conteo en el punto de encuentro', descripcion: c.propuesta }];
  });
}

// Al confirmar: obligatoria solo si el usuario la marca así (por defecto no).
export const editableDesdePropuesta = (p: PropuestaSimulacro, obligatoria = false): AccionEditable => ({
  sourceKey: p.sourceKey, sourceMethod: METODO_ACCION_SIMULACRO, sectorId: null, norma: p.norma, articulo: p.articulo,
  descripcion: p.descripcion, obligatoria, responsableId: null, responsableCargo: '', fecha: '', estado: 'pendiente',
});

// Siguiente clave libre para una acción que agrega el usuario desde los hallazgos (3 en adelante).
export function siguienteClaveManual(drillId: string, clavesGuardadas: string[]): string {
  const prefijo = `simulacro:${drillId}:`;
  let max = 2;
  for (const k of clavesGuardadas) {
    if (!k.startsWith(prefijo)) continue;
    const n = Number(k.slice(prefijo.length));
    if (Number.isInteger(n) && n > max) max = n;
  }
  return claveAccionSimulacro(drillId, max + 1);
}

// ── Validaciones del formulario ──────────────────────────────────────────────

export function validarProgramacion(s: Pick<FilaSimulacro, 'drill_type' | 'drill_type_other' | 'scheduled_date' | 'scope_all' | 'scope_sector_ids'>): string[] {
  const e: string[] = [];
  if (!TIPOS_SIMULACRO.includes(s.drill_type)) e.push('Elige el tipo de simulacro.');
  if (s.drill_type === 'otro' && !s.drill_type_other?.trim()) e.push('Describe el tipo de simulacro ("Otro").');
  if (!s.scheduled_date || !/^\d{4}-\d{2}-\d{2}$/.test(s.scheduled_date)) e.push('Elige la fecha programada.');
  if (!s.scope_all && s.scope_sector_ids.length === 0) e.push('Elige al menos un sector del plan o "Todo el centro".');
  return e;
}

export const MENSAJE_FECHA_FUTURA = 'La fecha realizada no puede ser futura: un simulacro se registra después de hacerlo.';

export function validarResultado(s: FilaSimulacro, hoy: Date = new Date()): string[] {
  const e = validarProgramacion({ ...s, scheduled_date: s.scheduled_date ?? s.performed_date });
  if (!s.performed_date || !/^\d{4}-\d{2}-\d{2}$/.test(s.performed_date)) e.push('Escribe la fecha en que se realizó.');
  else if (s.performed_date > isoLocal(hoy)) e.push(MENSAJE_FECHA_FUTURA); // PE-B2b
  for (const [campo, v] of [['participantes', s.participants_count], ['ocupantes esperados', s.expected_occupants], ['conteo en el punto de encuentro', s.assembly_count]] as const) {
    if (v !== null && (!Number.isInteger(v) || v < 0)) e.push(`El número de ${campo} no es válido.`);
  }
  if (s.participants_count === null) e.push('Escribe el número de participantes.');
  e.push(...validarTiempos(s));
  return e;
}

// Para firmar: realizado, con resultado completo y firma del responsable de SST (el representante es opcional).
export function faltantesParaFirmar(s: FilaSimulacro, firmas: Pick<FirmaInforme, 'rol' | 'nombre' | 'cedula'>[]): string[] {
  const e: string[] = [];
  if (s.status !== 'ejecutado') e.push('Solo se firma el informe de un simulacro realizado.');
  e.push(...validarResultado(s));
  if (tiempoTotal(s).segundos === null) e.push('Faltan la hora de la alarma y la del último en llegar al punto de encuentro.');
  const sst = firmas.find(f => f.rol === 'sst');
  if (!sst || !sst.nombre.trim() || !sst.cedula.trim()) e.push('Falta la firma del responsable de SST (nombre y cédula).');
  for (const f of firmas) if (f.cedula.trim() && !/^\d{10}$/.test(f.cedula.trim())) e.push(`La cédula de ${ETIQUETA_ROL_FIRMA[f.rol]} debe tener 10 dígitos.`);
  return Array.from(new Set(e));
}

// Texto canónico que se firma (hash SHA-256): datos del informe y del firmante.
export function contenidoParaFirma(s: FilaSimulacro, f: Pick<FirmaInforme, 'rol' | 'nombre' | 'cedula' | 'firmado_en'>): string {
  return JSON.stringify({
    simulacro: s.id, empresa: s.company_id, fecha: s.performed_date, tipo: s.drill_type, otro: s.drill_type_other, alcance: s.scope_all ? 'todo' : s.scope_sector_ids,
    tiempos: [s.alarm_time, s.evacuation_start_time, s.last_arrival_time], participantes: s.participants_count, conteo: s.assembly_count,
    hallazgos: s.incidents_notes, conclusion: s.conclusion, firmante: { rol: f.rol, nombre: f.nombre.trim(), cedula: f.cedula.trim(), en: f.firmado_en },
  });
}

// ── Informe anual ────────────────────────────────────────────────────────────

export interface AccionMinAnual { source_key: string | null; status: 'pendiente' | 'en_curso' | 'cerrada'; due_date: string | null }

export interface ResumenAnual {
  year: number;
  realizados: number; firmados: number; programados: number; cancelados: number;
  cumpleFrecuencia: boolean;            // al menos uno realizado con el informe firmado en el año (DE 394 Art. 68)
  tiempos: { con_tiempo: number; minimo_s: number | null; maximo_s: number | null; promedio_s: number | null };
  acciones: { total: number; abiertas: number; cerradas: number; vencidas: number };
}

// Simulacros del año (por la fecha realizada o, si no hay, la programada) y las acciones de mejora que salieron de ellos.
export function simulacrosDelAno<T extends Pick<FilaSimulacro, 'performed_date' | 'scheduled_date' | 'drill_date'>>(lista: T[], year: number): T[] {
  return lista.filter(s => fechaSimulacro(s).startsWith(`${year}-`));
}

export function resumenAnual(lista: FilaSimulacro[], acciones: AccionMinAnual[], year: number, hoy: Date = new Date()): ResumenAnual {
  const del = simulacrosDelAno(lista, year);
  const realizados = del.filter(s => s.status === 'ejecutado');
  const tiempos = realizados.map(s => tiempoTotal(s).segundos).filter((t): t is number => t !== null);
  const ids = new Set(del.map(s => s.id));
  const suyas = acciones.filter(a => esClaveSimulacro(a.source_key) && ids.has((a.source_key as string).split(':')[1]));
  const hoyIso = isoLocal(hoy);
  const abiertas = suyas.filter(a => a.status !== 'cerrada');
  return {
    year,
    realizados: realizados.length, firmados: realizados.filter(firmado).length,
    programados: del.filter(s => s.status === 'programado').length, cancelados: del.filter(s => s.status === 'cancelado').length,
    cumpleFrecuencia: realizados.some(firmado),
    tiempos: {
      con_tiempo: tiempos.length, minimo_s: tiempos.length ? Math.min(...tiempos) : null, maximo_s: tiempos.length ? Math.max(...tiempos) : null,
      promedio_s: tiempos.length ? Math.round(tiempos.reduce((a, b) => a + b, 0) / tiempos.length) : null,
    },
    acciones: {
      total: suyas.length, abiertas: abiertas.length, cerradas: suyas.length - abiertas.length,
      vencidas: abiertas.filter(a => !!a.due_date && a.due_date < hoyIso).length,
    },
  };
}

// ── PO-17 ────────────────────────────────────────────────────────────────────

export type EstadoPO17 = 'cumple' | 'en_proceso' | 'no_cumple';
type SimMin = Pick<FilaSimulacro, 'status' | 'drill_date' | 'performed_date' | 'scheduled_date' | 'report_signed_at'>;

// Mismo día del año anterior (29/02 → 28/02).
export function haceDoceMeses(hoy: Date): string {
  const y = hoy.getFullYear() - 1, m = hoy.getMonth();
  const ultimoDia = new Date(y, m + 1, 0).getDate();
  return isoLocal(new Date(y, m, Math.min(hoy.getDate(), ultimoDia)));
}

export interface ResultadoPO17 { estado: EstadoPO17; trazas: Traza[]; resumen: string }

// Cumple: al menos un realizado con informe firmado dentro de los últimos 12 meses (12 meses exactos cuentan).
// En proceso: sin eso, hay uno programado desde hoy en adelante o uno realizado sin firmar dentro de los 12 meses.
// No cumple: el resto (el último realizado tiene más de 12 meses, o no hay ninguno ni nada programado).
export function estadoPO17(simulacros: SimMin[], hoy: Date = new Date(), quito = false): ResultadoPO17 {
  const limite = haceDoceMeses(hoy), hoyIso = isoLocal(hoy);
  const realizados = simulacros.filter(s => s.status === 'ejecutado').map(s => ({ ...s, fecha: s.performed_date ?? s.drill_date }))
    .sort((a, b) => b.fecha.localeCompare(a.fecha));
  const vigentes = realizados.filter(s => s.fecha >= limite && s.fecha <= hoyIso);
  const firmadoVigente = vigentes.find(s => !!s.report_signed_at);
  const sinFirmar = vigentes.find(s => !s.report_signed_at);
  const programado = simulacros.filter(s => s.status === 'programado' && (s.scheduled_date ?? s.drill_date) >= hoyIso)
    .sort((a, b) => (a.scheduled_date ?? a.drill_date).localeCompare(b.scheduled_date ?? b.drill_date))[0];
  const ultimo = realizados[0];

  let estado: EstadoPO17, resumen: string;
  if (firmadoVigente) {
    estado = 'cumple'; resumen = `Simulacro realizado el ${fechaEc(firmadoVigente.fecha)} con el informe firmado (dentro de los últimos 12 meses).`;
  } else if (sinFirmar || programado) {
    estado = 'en_proceso';
    // PE-B2 (JC): un simulacro "ejecutado" sin informe firmado (p. ej. de la pantalla anterior) cuenta como realizado,
    // pero nunca da "Cumple".
    resumen = sinFirmar ? `Simulacro realizado sin informe firmado (${fechaEc(sinFirmar.fecha)}): cuenta como realizado, pero PO-17 no cumple hasta firmar el informe.`
      : `Simulacro programado para el ${fechaEc(programado!.scheduled_date ?? programado!.drill_date)}.`;
  } else {
    estado = 'no_cumple';
    resumen = ultimo ? `El último simulacro realizado (${fechaEc(ultimo.fecha)}${ultimo.report_signed_at ? '' : ', simulacro realizado sin informe firmado'}) tiene más de 12 meses y no hay otro programado.`
      : 'No hay simulacros realizados ni programados.';
  }
  const trazas: Traza[] = [{
    metodo: 'po17_frecuencia', norma: 'DE 394 (Reglamento de SST), Art. 68', articulo_o_tabla: 'al menos un simulacro al año, con su informe firmado',
    umbral: `desde ${fechaEc(limite)}`, unidad: 'fecha',
    valor: firmadoVigente ? fechaEc(firmadoVigente.fecha) : sinFirmar ? `${fechaEc(sinFirmar.fecha)} (sin informe firmado)` : ultimo ? fechaEc(ultimo.fecha) : null,
    estado: estado === 'cumple' ? 'cumple' : estado === 'no_cumple' ? 'no_cumple' : 'por_validar', fuente_doc: FUENTE_DE394_ART68, nota: resumen,
  }];
  if (quito) {
    trazas.push({
      metodo: 'po17_rtq3', norma: 'RTQ 3/2026 (Quito)', articulo_o_tabla: 'al menos un simulacro anual, documentado (industrial, oficinas, mercantil, educación)',
      umbral: 1, valor: vigentes.length, unidad: 'simulacros/año', estado: 'informativo', fuente_doc: FUENTE_RTQ3_SIMULACRO,
      nota: 'La RTQ 3 pide la misma frecuencia que el DE 394: no hay un umbral mayor. La sección 6 (almacenamiento) no tiene una regla propia de simulacros; las gasolineras (RTQ 4, 6.4 b) además los hace evaluar por el CB-DMQ (por validar).',
    });
  }
  return { estado, trazas, resumen };
}
